import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { proposalsService } from '@/services/proposals-service';
import { TemplateRenderer } from '@/pages/proposal-preview-page';
import { revisionCount } from '@/lib/revision-label';
import { SignaturePad } from '@/components/proposal/signature-pad';
import type { Proposal } from '@/pages/proposal-preview-page';
import { parseLchData, lchCostBySection, computeSectionFinancials, computeEstimateFinancials, parseSubcontractors, subcontractorCostBySection } from '@/lib/section-totals';
import { formatValidThrough, resolveValidThrough } from '@/lib/valid-through';

// Labels for about sections (same as proposal-preview-page)
const ABOUT_LABELS: Record<string, string> = {
  aboutCompany: 'About Company',
  mission: 'Company Mission',
  whoWeServe: 'Who We Serve',
  reasonsToChoose: 'Reasons to Choose Us',
  termsConditions: 'Terms and Conditions',
};

// Section key → label mapping for categories
const PROJECT_SECTIONS: { key: string; label: string }[] = [
  { key: 'asphalt', label: 'Asphalt' },
  { key: 'concrete', label: 'Concrete' },
  { key: 'grading', label: 'Grading' },
  { key: 'seal-coat', label: 'Seal Coat' },
  { key: 'striping', label: 'Striping' },
];

// Asset category → section key mapping
const ASSET_CAT_TO_SECTION: Record<string, string> = {
  asphalt: 'asphalt',
  concrete: 'concrete',
  grading: 'grading',
  sealcoat: 'seal-coat',
  striping: 'striping',
};

function fmtWithCommas(raw: string): string {
  if (!raw) return raw;
  const stripped = raw.replace(/,/g, '');
  const num = parseFloat(stripped);
  if (isNaN(num)) return raw;
  const parts = stripped.split('.');
  const intFormatted = parts[0] === '' ? '0' : Math.abs(parseInt(parts[0], 10)).toLocaleString('en-US');
  const sign = stripped.startsWith('-') ? '-' : '';
  return parts.length > 1 ? `${sign}${intFormatted}.${parts[1]}` : `${sign}${intFormatted}`;
}

// Render a writeup row's content array to a plain string
function renderRowContent(content: any[]): string {
  return content.map((item: any) => {
    if (typeof item === 'string') return item;
    if (item.placeholder === 'surface') return '';
    const val = item.value || item.placeholder || '___';
    return fmtWithCommas(val);
  }).join('');
}

// Build default writeup groups from projectTypes (fallback when no saved scopeData)
function buildDefaultWriteupGroups(projectTypes: string[]): any[] {
  const builders: Record<string, () => any> = {
    asphalt: () => ({
      id: 'asphalt', title: 'Asphalt', collapsed: false,
      rows: [], sections: [],
    }),
    grading: () => ({
      id: 'grading', title: 'Grading', collapsed: false,
      rows: [], sections: [],
    }),
    concrete: () => ({
      id: 'concrete', title: 'Concrete', collapsed: false,
      rows: [], sections: [],
    }),
    'seal-coat': () => ({
      id: 'seal-coat', title: 'Seal Coat', collapsed: false,
      rows: [], sections: [],
    }),
    striping: () => ({
      id: 'striping', title: 'Striping', collapsed: false,
      rows: [], sections: [],
    }),
  };
  const groups: any[] = [];
  for (const pt of projectTypes) {
    const builder = builders[pt];
    if (builder) groups.push(builder());
  }
  return groups;
}

type PageState = 'loading' | 'signing' | 'signed' | 'error';

export function ProposalSignaturePage() {
  const { shareableLink } = useParams<{ shareableLink: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [template, setTemplate] = useState('modern');
  const [signatureData, setSignatureData] = useState<{
    name: string;
    dataUrl: string;
    signedAt: string;
  } | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [downloading, setDownloading] = useState(false);

  // Format currency
  const fmt = (n: number) =>
    '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    if (!shareableLink) return;

    const loadProposal = async () => {
      try {
        const data = await proposalsService.getPublic(shareableLink);

        // Build the Proposal display object from the API response
        const est = data.estimate || {};
        const branding = data.tenant?.branding || {};
        const exclusionItems = data.tenant?.exclusionItems || [];
        const estimateAssets = est.assets || [];

        // Build pin letter lookup: assetId → letter (A, B, C...)
        const mapPins = est.mapPins || [];
        const pinLetterMap: Record<string, string> = {};
        mapPins.forEach((pin: any, idx: number) => {
          if (pin.photoAssetId) {
            let result = '';
            let n = idx;
            do {
              result = String.fromCharCode(65 + (n % 26)) + result;
              n = Math.floor(n / 26) - 1;
            } while (n >= 0);
            pinLetterMap[pin.photoAssetId] = result;
          }
        });

        const lineItems = (est.lineItems || []).map((li: any) => ({
          desc: li.description,
          qty: li.quantity?.toLocaleString('en-US') || '0',
          unit: li.unit || '',
          rate: fmt(li.rate || 0),
          total: fmt(li.lineTotal || 0),
        }));

        // Parse about sections from branding (exclude termsConditions — it gets its own section)
        let aboutSections: { label: string; text: string }[] = [];
        let termsText = '';
        if (branding.aboutUs) {
          try {
            const parsed = JSON.parse(branding.aboutUs);
            aboutSections = Object.entries(parsed)
              .filter(([key, val]: [string, any]) => key !== 'termsConditions' && val && val.includeInProposal && val.text?.trim())
              .map(([key, val]: [string, any]) => ({
                label: ABOUT_LABELS[key] || key,
                text: val.text,
              }));
            // Extract terms separately
            const tc = parsed.termsConditions;
            if (tc && tc.includeInProposal && tc.text?.trim()) termsText = tc.text;
          } catch {
            if (branding.aboutUs.trim()) {
              aboutSections = [{ label: 'About Us', text: branding.aboutUs }];
            }
          }
        }

        // Build inclusions and exclusions (only checked items from estimate)
        let sigInclIds: string[] = [];
        let sigExclIds: string[] = [];
        try { sigInclIds = JSON.parse(est.selectedInclusionIds || '[]'); } catch { /* ignore */ }
        try { sigExclIds = JSON.parse(est.selectedExclusionIds || '[]'); } catch { /* ignore */ }
        const inclusions = sigInclIds.length > 0
          ? exclusionItems.filter((i: any) => i.type === 'inclusion' && sigInclIds.includes(i.id)).map((i: any) => i.text)
          : [];
        const exclusions = sigExclIds.length > 0
          ? exclusionItems.filter((i: any) => i.type === 'exclusion' && sigExclIds.includes(i.id)).map((i: any) => i.text)
          : [];

        // Build asset lookup by category
        const assetsByCategory: Record<string, { url: string; caption?: string; description?: string | null }[]> = {};
        for (const asset of estimateAssets) {
          const cat = asset.category || 'map';
          if (!assetsByCategory[cat]) assetsByCategory[cat] = [];
          assetsByCategory[cat].push({ url: asset.url, caption: asset.fileName, description: (asset as any).description });
        }

        // Build categories from writeup data (scopeData) + line item totals
        const allItems = est.lineItems || [];

        // H&D cost per section — canonical parse/bucket shared with every surface.
        const lchBySection = lchCostBySection(parseLchData(est.lchData));
        // Per-service subcontractors fold into their service's category total.
        const subcontractorsBySection = subcontractorCostBySection(
          parseSubcontractors((est as any).subcontractorsData),
        );

        // Parse markupData for per-section markup calculation (folded in by the
        // shared computeSectionFinancials helper below).
        const markupDataRaw: Record<string, { markupPercent: number }[]> = (() => {
          try { return est.markupData || {}; } catch { return {}; }
        })();

        // Parse saved writeup groups from scopeData, or build defaults from projectTypes
        let scopeGroups: any[] = [];
        if (est.scopeData) {
          try {
            const parsed = JSON.parse(est.scopeData);
            if (Array.isArray(parsed) && parsed.length > 0) scopeGroups = parsed;
          } catch { /* ignore parse errors */ }
        }
        // Fallback: build default writeup groups from projectTypes if no saved data
        if (scopeGroups.length === 0) {
          let ptArr: string[] = [];
          try { ptArr = JSON.parse(est.projectTypes || '[]'); } catch { /* ignore */ }
          scopeGroups = buildDefaultWriteupGroups(ptArr);
        }

        type ProjectCategory = {
          name: string;
          groups: { name: string; items: string[] }[];
          categoryTotal: string;
          assets: { url: string; caption?: string }[];
        };
        const categories: ProjectCategory[] = [];

        for (const sec of PROJECT_SECTIONS) {
          const writeupGroup = scopeGroups.find((g: any) => g.id === sec.key);
          const sectionItems = allItems.filter((li: any) => {
            const s = (li.section as string) || '';
            if (sec.key === 'asphalt') return s === 'asphalt' || s === 'asphalt-grading' || s === 'asphalt-rnr' || s === 'asphalt-overlay';
            if (sec.key === 'concrete') return s === 'concrete' || s === 'concrete-grading';
            return s === sec.key;
          });
          const sectionTotal = computeSectionFinancials(
            sec.key, allItems, lchBySection, markupDataRaw, subcontractorsBySection,
          ).total;

          // A subcontractor-only service must still show — see the preview page.
          if (!writeupGroup && sectionItems.length === 0 && !subcontractorsBySection[sec.key]) continue;

          const groups: { name: string; items: string[] }[] = [];

          if (writeupGroup) {
            const directRows = (writeupGroup.rows || [])
              .filter((r: any) => r.enabled)
              .map((r: any) => renderRowContent(r.content));
            if (directRows.length > 0) {
              groups.push({ name: '', items: directRows });
            }
            for (const section of (writeupGroup.sections || [])) {
              const sectionRows = (section.rows || [])
                .filter((r: any) => r.enabled)
                .map((r: any) => renderRowContent(r.content));
              if (sectionRows.length > 0) {
                groups.push({ name: section.title, items: sectionRows });
              }
            }
          }

          if (sec.key === 'striping' && sectionItems.length > 0) {
            const lineItemStrings = sectionItems
              .filter((li: any) => !li.hideFromProposal)
              .map((li: any) => `${li.quantity} ${li.unit} — ${li.description}`);
            if (lineItemStrings.length > 0) groups.push({ name: '', items: lineItemStrings });
          }

          const sectionAssetKey = Object.entries(ASSET_CAT_TO_SECTION).find(([, v]) => v === sec.key)?.[0] || '';
          const sectionAssets = assetsByCategory[sectionAssetKey] || [];

          if (groups.length > 0 || sectionTotal > 0 || sectionAssets.length > 0) {
            categories.push({
              name: sec.label,
              groups,
              categoryTotal: fmt(sectionTotal),
              assets: sectionAssets,
            });
          }
        }

        const markupPct = est.defaultMarkup || data.markup || 0.15;

        // Grand total DERIVED from the same line items + markup + H&D + subcontractors
        // + discount the categories above are built from — never the stored `est.total`
        // scalar, which drifts stale. Guarantees the client-facing Final Base Bid Total
        // equals the sum of the category totals shown. (Unit pricing renders separately.)
        const estFin = est.isUnitPricing
          ? null
          : computeEstimateFinancials({
              lineItems: allItems as any,
              lchRows: parseLchData(est.lchData),
              markupData: markupDataRaw,
              subcontractors: parseSubcontractors((est as any).subcontractorsData),
              discountType: est.discountType,
              discountValue: est.discountValue,
            });

        const proposalDisplay: Proposal = {
          company: branding.companyName || '',
          brandColor: branding.primaryColor || '#3B82F6',
          phone: branding.companyPhone || '',
          email: branding.companyEmail || '',
          website: branding.companyWebsite || '',
          address: [branding.companyAddress, branding.companyCity, branding.companyState, branding.companyZip].filter(Boolean).join(', ') || '',
          license: '',
          logoUrl: branding.logoUrl || '',
          coverPhoto: branding.coverPhotoUrl || '/images/covers/cover-1.jpg',
          headerTintEnabled: branding.headerTintEnabled ?? true,
          client: data.clientCompany || data.clientName || '',
          clientContact: data.clientName || '',
          clientEmail: data.clientEmail || '',
          clientPhone: data.clientPhone || '',
          // Client mailing address comes from the linked client record, NOT the job site.
          clientAddress: [est.client?.address, est.client?.city, est.client?.state, est.client?.zip]
            .filter(Boolean).join(', '),
          clientAddressLine1: est.client?.address || '',
          clientAddressLine2: [est.client?.city, est.client?.state, est.client?.zip].filter(Boolean).join(', '),
          projectTitle: data.title || est.title || '',
          projectAddress: [est.jobAddress, est.jobCity, est.jobState, est.jobZip]
            .filter(Boolean).join(', '),
          date: data.createdAt
            ? new Date(data.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
            : '',
          // `est` is snapshot-applied by the API (proposal-service getByShareableLink),
          // so this is the date frozen when the customer was sent the proposal.
          // data.expiresAt is only the creation-time copy — see lib/valid-through.ts.
          validUntil: formatValidThrough(resolveValidThrough(est, data)) ?? '',
          proposalNumber: data.proposalNumber || '',
          revisionCount: revisionCount(data.revisionNumber),
          // Submitted By stays as the ORIGINAL estimator (estimate's createdByUser),
          // not whoever created the proposal record or revised it.
          estimatorName: est.createdByUser?.displayName || data.createdByUser?.displayName || '',
          scope: est.scopeOfWork || '',
          lineItems,
          categories,
          subtotal: fmt(est.subtotal || 0),
          markup: (markupPct * 100).toFixed(0) + '%',
          markupAmount: fmt(est.markupAmount || 0),
          total: fmt(estFin ? estFin.grandTotalBeforeDiscount : (est.total || 0)),
          discountType: est.discountType || 'percentage',
          discountValue: est.discountValue || 0,
          showDiscountOnProposal: est.showDiscountOnProposal || false,
          discountAmount: (() => {
            if (estFin) return estFin.discountAmount > 0 ? fmt(estFin.discountAmount) : undefined;
            const dv = est.discountValue || 0;
            if (dv <= 0) return undefined;
            const dt = est.discountType || 'percentage';
            const da = dt === 'percentage' ? (est.total || 0) * (dv / 100) : dv;
            return fmt(da);
          })(),
          finalTotal: (() => {
            if (estFin) return fmt(estFin.total);
            const dv = est.discountValue || 0;
            if (dv <= 0) return fmt(est.total || 0);
            const dt = est.discountType || 'percentage';
            const da = dt === 'percentage' ? (est.total || 0) * (dv / 100) : dv;
            return fmt(Math.max(0, (est.total || 0) - da));
          })(),
          aboutCompany: '',
          aboutSections,
          terms: termsText,
          inclusions,
          exclusions,
          additionalNotes: est.additionalNotes || '',
          projectConditions: est.projectConditions || '',
          mapAssets: assetsByCategory['map'] || [],
          addendumAssets: assetsByCategory['addendum'] || [],
          pinLetters: Object.keys(pinLetterMap).length > 0 ? pinLetterMap : undefined,
          isUnitPricing: !!est.isUnitPricing,
          unitPriceItems: est.isUnitPricing
            ? (est.lineItems || [])
                .filter((li: any) => li.section === 'unit-price')
                .map((li: any) => ({
                  item: li.notes || '',
                  description: li.description || '',
                  unitType: li.unit || 'Each',
                  unitPrice: li.rate || 0,
                  quantity: li.quantity || 0,
                  total: li.lineTotal || 0,
                }))
            : undefined,
          options: (() => {
            const raw = (est as any).optionsData;
            if (!raw || !Array.isArray(raw) || raw.length === 0) return undefined;
            return raw.map((r: any) => ({ id: r.id || '', name: r.name || '', description: r.description || '', cost: Number(r.cost) || 0 }));
          })(),
        };

        setProposal(proposalDisplay);
        setTemplate(data.templateSlug || 'classic');

        // Check if already signed
        if (data.signedAt && data.signatureDataUrl) {
          setSignatureData({
            name: data.signatureName || '',
            dataUrl: data.signatureDataUrl,
            signedAt: new Date(data.signedAt).toLocaleDateString('en-US', {
              year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
            }),
          });
          setPageState('signed');
        } else {
          setPageState('signing');
        }
      } catch (err: any) {
        setErrorMsg(err?.response?.data?.message || 'Proposal not found');
        setPageState('error');
      }
    };

    loadProposal();
  }, [shareableLink]);

  const handleSign = async (signatureName: string, signatureDataUrl: string) => {
    if (!shareableLink) return;
    try {
      await proposalsService.signPublic(shareableLink, signatureName, signatureDataUrl);
      setSignatureData({
        name: signatureName,
        dataUrl: signatureDataUrl,
        signedAt: new Date().toLocaleDateString('en-US', {
          year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
        }),
      });
      setPageState('signed');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to submit signature. Please try again.';
      alert(msg);
    }
  };

  const handleDownloadPdf = async () => {
    if (!shareableLink || downloading) return;
    try {
      setDownloading(true);
      const blob = await proposalsService.downloadPublicPdf(shareableLink);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Proposal-${proposal?.proposalNumber || 'signed'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('PDF download error:', err?.response?.status, err?.message);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="pp-sig-page-loading">
        <div className="pp-sig-spinner" />
        <p>Loading proposal...</p>
      </div>
    );
  }

  // Error state
  if (pageState === 'error' || !proposal) {
    return (
      <div className="pp-sig-page-error">
        <h2>Proposal Not Found</h2>
        <p>{errorMsg || 'This proposal link may be invalid or expired.'}</p>
      </div>
    );
  }

  return (
    <div className="pp-sig-page">
      {/* Minimal topbar */}
      <div className="pp-sig-topbar" style={{ borderBottomColor: proposal.brandColor }}>
        <div className="pp-sig-topbar-left">
          {proposal.logoUrl && (
            <img src={proposal.logoUrl} alt={proposal.company} className="pp-sig-topbar-logo" />
          )}
          <span className="pp-sig-topbar-company">{proposal.company}</span>
        </div>
        <div className="pp-sig-topbar-right">
          <span className="pp-sig-topbar-number">Proposal #{proposal.proposalNumber}{(proposal.revisionCount ?? 0) > 0 ? ` · Revision ${proposal.revisionCount}` : ''}</span>
          {pageState === 'signing' && (
            <button
              className="pp-sig-cta"
              style={{ backgroundColor: proposal.brandColor }}
              onClick={() => document.getElementById('signature-pad-section')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Sign Proposal
            </button>
          )}
          {pageState === 'signed' && (
            <button
              className="pp-sig-cta"
              style={{ backgroundColor: proposal.brandColor }}
              onClick={handleDownloadPdf}
              disabled={downloading}
            >
              {downloading ? 'Generating PDF...' : 'Download PDF'}
            </button>
          )}
        </div>
      </div>

      {/* Signed confirmation banner */}
      {pageState === 'signed' && signatureData && (
        <div className="pp-sig-banner">
          <div className="pp-sig-banner-icon">
            <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" width="24" height="24">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <div>
            <h3>Proposal Signed Successfully</h3>
            <p>Signed by {signatureData.name} on {signatureData.signedAt}</p>
          </div>
        </div>
      )}

      {/* Proposal content */}
      <div className="pp-canvas">
        <TemplateRenderer template={template} proposal={proposal} />
      </div>

      {/* Signature area */}
      <div className="pp-sig-section" id="signature-pad-section">
        {pageState === 'signed' && signatureData ? (
          <div className="pp-sig-signed-display">
            <h3>Client Signature</h3>
            <img src={signatureData.dataUrl} alt="Signature" className="pp-sig-image" />
            <p className="pp-sig-signed-name">{signatureData.name}</p>
            <p className="pp-sig-signed-date">Signed: {signatureData.signedAt}</p>
          </div>
        ) : (
          <SignaturePad
            onSign={handleSign}
            brandColor={proposal.brandColor}
            companyName={proposal.company}
          />
        )}
      </div>
    </div>
  );
}

